// 📚 References → Web Search References (contract §6ai). Searches the trusted
// medical websites the hospital configured (Admin → Clinical → Reference
// Library → Web search) and shows the real results with links, plus a short
// summary citing them. Only the question leaves the hospital: the server
// refuses one carrying the open patient's name/UHID, a phone number or an ID.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, History, RotateCcw, Search, ShieldCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import VoiceMicButton from '@/components/VoiceMicButton';
import { keys } from '@/lib/queryKeys';
import { fetchWebSearchHistory, fetchWebSearchQuery, fetchWebSearchStatus, runWebSearch, type WebSearchAnswer } from './api';
import { WebSearchAnswerView } from './WebSearchAnswerView';

const SITES_SHOWN = 6;

export function WebSearchPanel({ active, visitId = null, admissionId = null }: {
  active: boolean;
  visitId?: number | null;
  admissionId?: number | null;
}) {
  const qc = useQueryClient();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<WebSearchAnswer | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showAllSites, setShowAllSites] = useState(false);
  const status = useQuery({ queryKey: keys.clinical.webSearchStatus(), queryFn: fetchWebSearchStatus, enabled: active, staleTime: 60_000 });
  const history = useQuery({ queryKey: keys.clinical.webSearchHistory(), queryFn: fetchWebSearchHistory, enabled: active && showHistory });
  const search = useMutation({
    mutationFn: (q: string) => runWebSearch({ question: q, visit_id: visitId, admission_id: admissionId }),
    onSuccess: (a) => { setAnswer(a); qc.invalidateQueries({ queryKey: keys.clinical.webSearchHistory() }); },
  });
  const reopen = useMutation({ mutationFn: fetchWebSearchQuery, onSuccess: (a) => { setAnswer(a); setShowHistory(false); } });

  // A new patient is a new search.
  useEffect(() => { setAnswer(null); search.reset(); }, [visitId, admissionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = status.data;
  const canSearch = !!s?.available;
  const submit = () => { const q = question.trim(); if (canSearch && q.length >= 3 && !search.isPending) search.mutate(q); };
  const clear = () => { setAnswer(null); setQuestion(''); search.reset(); };
  const error = search.error instanceof Error ? search.error.message : null;
  const sites = s?.sites ?? null;

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-[12.5px]">
        <div className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-ink">
          <Globe className="h-4 w-4 text-success" /> Trusted medical web sources
        </div>
        <p className="text-ink-muted">Search configured medical websites and display real results with source links.</p>
        <p className="text-ink-muted">Sources may include WHO, CDC, PubMed and approved government health websites.</p>
        {sites && sites.length > 0 && (
          <div className="mt-1.5 text-[11.5px] text-ink-muted">
            Searching only: {(showAllSites ? sites : sites.slice(0, SITES_SHOWN)).join(', ')}
            {sites.length > SITES_SHOWN && (
              <button type="button" className="ml-1 font-medium text-teal hover:underline" onClick={() => setShowAllSites((v) => !v)}>
                {showAllSites ? 'show fewer' : `+${sites.length - SITES_SHOWN} more`}
              </button>
            )}
            {s?.provider_label ? ` · via ${s.provider_label}` : ''}
          </div>
        )}
      </div>

      {status.isLoading && <LoadingState />}
      {status.isError && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">Couldn&rsquo;t check whether web search is available. Close and reopen References to try again.</div>}
      {s && !s.available && <div className="rounded-lg bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">{s.message}</div>}

      {s?.available && (
        <>
          <div className="flex items-start gap-1.5 text-[11.5px] text-ink-muted">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal" />
            <span>Only your question is sent outside the hospital — no patient details. Don&rsquo;t type the patient&rsquo;s name, phone number or ID; a question containing them is refused.</span>
          </div>
          <div className="flex flex-col gap-2">
            <Textarea
              value={question} onChange={(e) => setQuestion(e.target.value)} rows={3} maxLength={300}
              placeholder="e.g. Dengue warning signs and fluid management in adults"
              aria-label="Your question for the web search"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <VoiceMicButton translateToEnglish title="Speak your question" onText={(t) => setQuestion((q) => (q ? `${q} ${t}` : t))} />
              <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}><History className="mr-1 h-3.5 w-3.5" /> Earlier searches</Button>
              {(answer || error) && <Button variant="ghost" size="sm" onClick={clear}><X className="mr-1 h-3.5 w-3.5" /> Clear</Button>}
              <Button className="ml-auto" size="sm" loading={search.isPending} disabled={question.trim().length < 3} onClick={submit}>
                <Search className="mr-1 h-3.5 w-3.5" /> Search
              </Button>
            </div>
            {search.isPending && <div className="text-[12px] text-ink-muted">Searching trusted medical websites…</div>}
            {error && (
              <div className="flex items-start justify-between gap-2 rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">
                <span>{error}</span>
                <Button variant="ghost" size="sm" className="shrink-0" onClick={() => search.variables && search.mutate(search.variables)} disabled={search.isPending}>
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Retry
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {showHistory && (
        <div className="rounded-lg border border-border">
          {history.isLoading && <div className="px-3 py-2 text-[12px] text-ink-muted">Loading…</div>}
          {history.data?.length === 0 && <div className="px-3 py-2 text-[12px] text-ink-muted">No earlier searches.</div>}
          <ul className="max-h-56 divide-y divide-border overflow-auto">
            {history.data?.map((h) => (
              <li key={h.id}>
                <button type="button" className="w-full px-3 py-1.5 text-left text-[12.5px] hover:bg-muted" onClick={() => reopen.mutate(h.id)}>
                  <span className="block truncate text-ink">{h.question}</span>
                  <span className="text-[11px] text-ink-muted">
                    {new Date(h.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {h.status === 'ok' ? ` · ${h.result_count} source${h.result_count === 1 ? '' : 's'}` : h.status === 'no_results' ? ' · no results' : ' · failed'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {answer && (
        <div className="flex flex-col gap-2">
          <div className="text-[12px] text-ink-muted">You searched: <span className="text-ink">{answer.question}</span></div>
          {answer.status === 'error'
            ? <div className="rounded-md bg-muted px-3 py-2 text-[12.5px] text-ink-muted">This search failed when it was run, so there are no results to show.</div>
            : <WebSearchAnswerView answer={answer} />}
        </div>
      )}
    </div>
  );
}
